import React, { useCallback, useEffect, useRef, useState } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup, Marker } from "react-simple-maps";
import { geoCentroid } from "d3-geo";
import { Plus, Minus, RotateCcw, Maximize, Minimize, Move } from "lucide-react";
import { cn } from "@/lib/utils";

// Shared US state choropleth (geoAlbersUsa). Reused by the admin SITREP drill map and the
// public /map coverage page so wheel/pinch handling lives in exactly ONE place (item 8 fix).
export const US_STATES_URL = "/geo/us-states.json";
const US_CENTER = [-96.5, 38.7];
const HINT_KEY = "os_map_gesture_hint"; // sessionStorage: hint shown once per session

export const US_NAME_TO_CODE = {
  Alabama:"AL",Alaska:"AK",Arizona:"AZ",Arkansas:"AR",California:"CA",Colorado:"CO",Connecticut:"CT",
  Delaware:"DE","District of Columbia":"DC",Florida:"FL",Georgia:"GA",Hawaii:"HI",Idaho:"ID",Illinois:"IL",
  Indiana:"IN",Iowa:"IA",Kansas:"KS",Kentucky:"KY",Louisiana:"LA",Maine:"ME",Maryland:"MD",Massachusetts:"MA",
  Michigan:"MI",Minnesota:"MN",Mississippi:"MS",Missouri:"MO",Montana:"MT",Nebraska:"NE",Nevada:"NV",
  "New Hampshire":"NH","New Jersey":"NJ","New Mexico":"NM","New York":"NY","North Carolina":"NC","North Dakota":"ND",
  Ohio:"OH",Oklahoma:"OK",Oregon:"OR",Pennsylvania:"PA","Rhode Island":"RI","South Carolina":"SC","South Dakota":"SD",
  Tennessee:"TN",Texas:"TX",Utah:"UT",Vermont:"VT",Virginia:"VA",Washington:"WA","West Virginia":"WV",
  Wisconsin:"WI",Wyoming:"WY","Puerto Rico":"PR",
};

const zoomBtn = "border border-sniper px-2 py-1 hover:border-white/40 text-foreground inline-flex items-center gap-1 bg-black/70";

/**
 * Presentational only. All data + shading decisions come from props so callers stay in control
 * of what a state means (sessions vs. verified-seller density vs. businesses).
 *
 * Props:
 *  - datumForCode(code) -> datum | null
 *  - styleFor(geo, code, datum) -> { default, hover, pressed }
 *  - labelFor(datum) -> string | null   (rendered at the state centroid)
 *  - onSelect(code, datum, geo)
 *  - stateTestId(code, geoName) -> string
 *  - svgTestId, labelColor, height, zoomable
 */
export default function UsChoroplethMap({
  datumForCode, styleFor, labelFor, onSelect,
  stateTestId = (code, name) => `state-${code || name}`,
  svgTestId = "us-choropleth-svg",
  labelColor = "#DAD6CE",
  height = 560,
  zoomable = false,
  children,
}) {
  const wrapRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [center, setCenter] = useState(US_CENTER);
  const [mapKey, setMapKey] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [hint, setHint] = useState(null); // null | "in" | "out"

  const hintSeenRef = useRef(false);
  const hintFadeRef = useRef(null);
  const hintDropRef = useRef(null);

  // item-8 fix: a two-finger PINCH arrives as a ctrlKey wheel event; a two-finger SCROLL as a
  // plain wheel. Both would otherwise scroll/zoom the PAGE. On the zoomable map we preventDefault
  // every wheel so d3-zoom handles it and the page stays put. Implemented ONCE, here.
  useEffect(() => {
    if (!zoomable) return;
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (e) => e.preventDefault();
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoomable]);

  // First one-finger touch on the map: nudge the user toward the two-finger gesture. Shown ONCE
  // per browser session (sessionStorage — a return visit next week gets the reminder again). The
  // overlay is pointer-events:none so it never eats the very gesture we're teaching.
  const maybeShowHint = useCallback(() => {
    if (hintSeenRef.current) return;
    try { if (sessionStorage.getItem(HINT_KEY) === "1") { hintSeenRef.current = true; return; } } catch {}
    hintSeenRef.current = true;
    try { sessionStorage.setItem(HINT_KEY, "1"); } catch {}
    setHint("in");
    clearTimeout(hintFadeRef.current);
    clearTimeout(hintDropRef.current);
    hintFadeRef.current = setTimeout(() => setHint("out"), 1800);
    hintDropRef.current = setTimeout(() => setHint(null), 2300);
  }, []);

  useEffect(() => () => { clearTimeout(hintFadeRef.current); clearTimeout(hintDropRef.current); }, []);

  // COOPERATIVE GESTURES (touch): dynamically drive `touch-action` on the wrapper so the page and
  // the map share the surface. 1 finger => touch-action:auto (the PAGE scrolls, the map stays put).
  // 2+ fingers => touch-action:none (d3-zoom gets the pan + pinch). In fullscreen there is no page
  // to scroll, so one finger pans and touch-action stays none. Reset on BOTH touchend AND
  // touchcancel — a phone call / notification fires touchcancel, and leaving it at `none` would
  // silently make the page unscrollable until reload.
  useEffect(() => {
    if (!zoomable) return;
    const el = wrapRef.current;
    if (!el) return;

    const applyTA = (touchCount) => {
      el.style.touchAction = fullscreen ? "none" : (touchCount >= 2 ? "none" : "auto");
    };
    const onStart = (e) => {
      applyTA(e.touches.length);
      if (!fullscreen && e.touches.length === 1) maybeShowHint();
    };
    const onEndOrCancel = (e) => { applyTA(e.touches.length); };

    // set the baseline immediately (so the first single-finger pan in fullscreen works)
    el.style.touchAction = fullscreen ? "none" : "auto";

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchend", onEndOrCancel, { passive: true });
    el.addEventListener("touchcancel", onEndOrCancel, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchend", onEndOrCancel);
      el.removeEventListener("touchcancel", onEndOrCancel);
    };
  }, [zoomable, fullscreen, maybeShowHint]);

  // d3-zoom gesture filter: ignore a single-touch DRAG (that belongs to the page) but let a
  // multi-touch pan + pinch through. In fullscreen a single finger pans. Desktop is unchanged —
  // primary-button mouse drag + wheel (incl. trackpad pinch = ctrlKey wheel) keep working.
  const zoomFilter = useCallback((e) => {
    if (!e) return false;
    if (e.type === "wheel") return !e.button;
    if (e.touches) return fullscreen ? e.touches.length >= 1 : e.touches.length >= 2;
    return !e.button && !e.ctrlKey;
  }, [fullscreen]);

  // Fullscreen: the CSS fixed overlay is the SOURCE OF TRUTH (iPhone Safari has no element
  // Fullscreen API, so the fallback is what actually runs for most phones). Native
  // requestFullscreen is a progressive ENHANCEMENT layered on top where supported (Android
  // Chrome / desktop) to also hide the browser chrome.
  const toggleFullscreen = useCallback(() => {
    setFullscreen((f) => {
      const next = !f;
      const el = wrapRef.current;
      try {
        if (next) {
          if (el && el.requestFullscreen) el.requestFullscreen().catch(() => {});
          else if (el && el.webkitRequestFullscreen) el.webkitRequestFullscreen();
        } else {
          if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(() => {});
          else if (document.webkitFullscreenElement && document.webkitExitFullscreen) document.webkitExitFullscreen();
        }
      } catch {}
      return next;
    });
  }, []);

  // Sync state OFF when the user leaves native fullscreen via Esc / the OS back gesture.
  useEffect(() => {
    const onFsChange = () => {
      const active = document.fullscreenElement || document.webkitFullscreenElement;
      if (!active) setFullscreen((f) => (f ? false : f));
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
    };
  }, []);

  // Esc also exits the CSS-only overlay (iPhone Safari, where no native fullscreenchange fires).
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => { if (e.key === "Escape") setFullscreen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen]);

  const nudge = (factor) => setZoom((z) => { const nz = Math.max(1, Math.min(12, z * factor)); setMapKey((k) => k + 1); return nz; });
  const reset = () => { setZoom(1); setCenter(US_CENTER); setMapKey((k) => k + 1); };

  const renderStates = ({ geographies }) => geographies.map((geo) => {
    const code = US_NAME_TO_CODE[geo.properties.name];
    const datum = code ? (datumForCode ? datumForCode(code) : null) : null;
    const centroid = geoCentroid(geo);
    const label = labelFor ? labelFor(datum) : null;
    return (
      <React.Fragment key={geo.rsmKey}>
        <Geography
          geography={geo}
          onClick={() => onSelect && onSelect(code, datum, geo)}
          data-testid={stateTestId(code, geo.properties.name)}
          style={styleFor(geo, code, datum)}
        />
        {label != null && (
          <Marker coordinates={centroid} style={{ default: { pointerEvents: "none" } }}>
            <text textAnchor="middle" y={3} fill={labelColor} fontSize={11 / (zoomable ? zoom : 1)} fontWeight="bold" fontFamily="monospace"
              style={{ pointerEvents: "none", paintOrder: "stroke", stroke: "#0A0A0A", strokeWidth: 2.5 / (zoomable ? zoom : 1) }}>
              {label}
            </text>
          </Marker>
        )}
      </React.Fragment>
    );
  });

  const safeAreaStyle = fullscreen ? {
    paddingTop: "env(safe-area-inset-top)",
    paddingBottom: "env(safe-area-inset-bottom)",
    paddingLeft: "env(safe-area-inset-left)",
    paddingRight: "env(safe-area-inset-right)",
  } : undefined;

  return (
    <div
      ref={wrapRef}
      className={cn("relative", fullscreen && "fixed inset-0 z-[9999] bg-black flex items-center justify-center")}
      style={safeAreaStyle}
      data-fullscreen={fullscreen || undefined}
      data-testid={zoomable ? "map-interactive-wrap" : undefined}
    >
      {zoomable && (
        <div
          className="absolute z-20 flex flex-col gap-1"
          style={fullscreen
            ? { top: "calc(env(safe-area-inset-top) + 12px)", right: "calc(env(safe-area-inset-right) + 12px)" }
            : { top: 12, right: 12 }}
          data-testid="map-zoom-controls"
        >
          <button onClick={() => nudge(1.6)} data-testid="map-zoom-in" className={zoomBtn} title="Zoom in"><Plus className="w-3.5 h-3.5" /></button>
          <button onClick={() => nudge(1 / 1.6)} data-testid="map-zoom-out" className={zoomBtn} title="Zoom out"><Minus className="w-3.5 h-3.5" /></button>
          <button onClick={reset} data-testid="map-zoom-reset" className={zoomBtn} title="Reset view"><RotateCcw className="w-3.5 h-3.5" /></button>
          <button onClick={toggleFullscreen} data-testid="map-fullscreen-toggle" className={zoomBtn} title={fullscreen ? "Exit fullscreen" : "Fullscreen"}>
            {fullscreen ? <Minimize className="w-3.5 h-3.5" /> : <Maximize className="w-3.5 h-3.5" />}
          </button>
        </div>
      )}

      <ComposableMap projection="geoAlbersUsa" width={980} height={height} style={{ width: "100%", height: "auto" }} data-testid={svgTestId}>
        {zoomable ? (
          <ZoomableGroup key={mapKey} center={center} zoom={zoom} minZoom={1} maxZoom={12}
            filterZoomEvent={zoomFilter}
            onMoveEnd={({ coordinates, zoom: z }) => { if (coordinates) setCenter(coordinates); if (z) setZoom(z); }}>
            <Geographies geography={US_STATES_URL}>{renderStates}</Geographies>
            {children}
          </ZoomableGroup>
        ) : (
          <Geographies geography={US_STATES_URL}>{renderStates}</Geographies>
        )}
      </ComposableMap>

      {/* Touch hint — pointer-events:none so the first two-finger attempt lands on the MAP, not here. */}
      {zoomable && hint && (
        <div
          className={cn(
            "absolute inset-0 z-30 flex items-center justify-center pointer-events-none transition-opacity duration-500",
            hint === "out" ? "opacity-0" : "opacity-100"
          )}
          data-testid="map-gesture-hint"
          aria-hidden="true"
        >
          <div className="flex items-center gap-2 bg-black/85 border border-olive/50 px-4 py-2.5 font-mono text-[11px] uppercase tracking-wide text-foreground">
            <Move className="w-4 h-4 text-olive-bright shrink-0" strokeWidth={1.8} /> Use two fingers to move the map
          </div>
        </div>
      )}
    </div>
  );
}
